import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCopyToClipboard } from '@/lib/clipboard';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CopyButtonProps {
  /** The text to copy to clipboard */
  textToCopy: string;
  /** Label describing what is being copied (e.g., "Email Address", "Token") */
  label: string;
  children?: string;
  /** Additional CSS classes */
  className?: string;
  /** Button variant */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  /** Button size */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /** Duration to show copied state in milliseconds */
  copiedDuration?: number;
}

export function CopyButton({
  textToCopy,
  label,
  children,
  className,
  variant = 'ghost',
  size = 'sm',
  copiedDuration = 2000,
}: CopyButtonProps) {
  const [copy, isCopied] = useCopyToClipboard(() => textToCopy, copiedDuration);

  const isIconOnly = !children;

  return (
    <Tooltip open={isCopied ? true : undefined}>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={isIconOnly ? 'icon' : size}
          onClick={copy}
          className={cn(
            isIconOnly && 'h-8 w-8 p-0',
            'text-gray-400 transition-colors hover:text-white hover:bg-muted/10',
            isCopied && 'text-green-500 hover:text-green-400',
            className
          )}
        >
          {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {children && <span className="ml-2">{isCopied ? 'Copied!' : children}</span>}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isCopied ? `${label} copied to clipboard` : `Copy ${label} to clipboard`}
      </TooltipContent>
    </Tooltip>
  );
}
